import {
  Checkbox,
  Group,
  Loader,
  Pagination,
  Select,
  Table,
  Text,
} from "@mantine/core";
import React from "react";

const getNestedValue = (obj, path) => {
  if (!path) return obj;
  if (typeof path === "string") {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  }
  if (Array.isArray(path)) {
    return path.reduce((acc, key) => acc?.[key], obj);
  }
  return obj;
};

const DataTable = ({
  columns = [],
  dataSource = [],
  loading = false,
  pagination = false,
  rowKey = "_id",
  scroll,
  rowSelection,
  footer,
}) => {
  const getKey = (record, index) => {
    if (typeof rowKey === "function") return rowKey(record);
    return record?.[rowKey] ?? index;
  };

  const allKeys = (dataSource || []).map((r, i) => getKey(r, i));
  const allSelected =
    rowSelection &&
    allKeys.length > 0 &&
    allKeys.every((k) => rowSelection.selectedRowKeys?.includes(k));

  const colCount = columns.filter(Boolean).length + (rowSelection ? 1 : 0);
  const tableMinWidth = scroll?.x || colCount * 160;

  return (
    <div className="datatable-wrapper table-card">
      {loading && (
        <div className="flex justify-center py-12">
          <Loader size="md" />
        </div>
      )}
      {!loading && (
        <>
          <div className="datatable-scroll">
            <Table
              striped
              highlightOnHover
              withTableBorder
              withColumnBorders
              className="datatable-table"
              style={{ minWidth: tableMinWidth }}
            >
              <Table.Thead>
                <Table.Tr>
                  {rowSelection && (
                    <Table.Th w={40}>
                      <Checkbox
                        checked={allSelected}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            rowSelection.onChange(allKeys);
                          } else {
                            rowSelection.onChange([]);
                          }
                        }}
                      />
                    </Table.Th>
                  )}
                  {columns.filter(Boolean).map((col) => (
                    <Table.Th key={col.key || col.dataIndex}>
                      {col.title}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(!dataSource || dataSource.length === 0) && (
                  <Table.Tr>
                    <Table.Td
                      colSpan={
                        columns.filter(Boolean).length +
                        (rowSelection ? 1 : 0)
                      }
                    >
                      <Text ta="center" c="dimmed" py="lg">
                        No data
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {(dataSource || []).map((record, index) => {
                  const key = getKey(record, index);
                  return (
                    <Table.Tr key={key}>
                      {rowSelection && (
                        <Table.Td>
                          <Checkbox
                            checked={rowSelection.selectedRowKeys?.includes(key)}
                            onChange={(e) => {
                              const checked = e.currentTarget.checked;
                              const next = checked
                                ? [...(rowSelection.selectedRowKeys || []), key]
                                : (rowSelection.selectedRowKeys || []).filter(
                                    (k) => k !== key
                                  );
                              rowSelection.onChange(next);
                            }}
                          />
                        </Table.Td>
                      )}
                      {columns.filter(Boolean).map((col) => {
                        const value = getNestedValue(record, col.dataIndex);
                        return (
                          <Table.Td key={col.key || col.dataIndex}>
                            {col.render
                              ? col.render(value, record, index)
                              : value != null
                              ? String(value)
                              : ""}
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </div>

          {footer && typeof footer === "function" && (
            <div className="mt-4 p-2">{footer()}</div>
          )}
          {pagination && pagination.total > 0 && (
            <Group justify="space-between" mt="md">
              <Text size="sm" c="dimmed">
                {pagination.showTotal
                  ? pagination.showTotal(pagination.total, [
                      (pagination.current - 1) * pagination.pageSize + 1,
                      Math.min(
                        pagination.current * pagination.pageSize,
                        pagination.total
                      ),
                    ])
                  : `Total ${pagination.total} items`}
              </Text>
              <Group gap="sm">
                {pagination.showSizeChanger && pagination.pageSizeOptions && (
                  <Select
                    size="xs"
                    w={100}
                    value={String(pagination.pageSize)}
                    data={pagination.pageSizeOptions.map((s) => ({
                      value: String(s),
                      label: `${s} / page`,
                    }))}
                    onChange={(val) => {
                      const size = Number(val);
                      pagination.onChange?.(1, size);
                      pagination.onShowSizeChange?.(1, size);
                    }}
                  />
                )}
                <Pagination
                  total={Math.ceil(
                    pagination.total / (pagination.pageSize || 10)
                  )}
                  value={pagination.current || 1}
                  onChange={(page) =>
                    pagination.onChange?.(page, pagination.pageSize)
                  }
                  size="sm"
                />
              </Group>
            </Group>
          )}
        </>
      )}
    </div>
  );
};

export default DataTable;
